import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAirModal } from '@air/provider-modal';
import { resetSelectedItemsAction } from '@air/redux-selected-items';

import { BulkDownloadAssetsModal } from '~/components/Modals/BulkDownloadAssetsModal';
import {
  selectedBoardsSelector,
  selectedFileClipsSelector,
  selectedMediaClipsSelector,
} from '~/store/selectedItems/selectors';

export const useSelectionBarDownloads = () => {
  const [showBulkDownloadAssetsModal] = useAirModal(BulkDownloadAssetsModal);
  const dispatch = useDispatch();
  const selectedBoardItems = useSelector(selectedBoardsSelector);
  const selectedMediaClipItems = useSelector(selectedMediaClipsSelector);
  const selectedFileItems = useSelector(selectedFileClipsSelector);

  const createBulkDownloadHandler = useCallback(
    <TDownloadItems extends CallableFunction, TBulkDownload extends CallableFunction>(options: {
      downloadItems: TDownloadItems;
      bulkDownloadAssets: TBulkDownload;
      additionalParams?: Record<string, unknown>;
    }) => {
      return () => {
        const { downloadItems, bulkDownloadAssets, additionalParams = {} } = options;

        const allClipIds = [...selectedMediaClipItems.map(({ id }) => id), ...selectedFileItems.map(({ id }) => id)];

        if (selectedBoardItems.length > 0) {
          downloadItems({
            clipIds: allClipIds,
            boardIds: selectedBoardItems.map(({ id }) => id),
            trackLocation: 'action-bar',
            ...additionalParams,
          });
        } else {
          showBulkDownloadAssetsModal({
            clipIds: allClipIds,
            onDownloadAsZip: () => {
              downloadItems({
                clipIds: allClipIds,
                trackLocation: 'action-bar',
                ...additionalParams,
              });
              dispatch(resetSelectedItemsAction());
            },
            onDownloadIndividually: () => {
              bulkDownloadAssets({
                clipIds: allClipIds,
                trackLocation: 'action-bar',
                ...additionalParams,
              });
              dispatch(resetSelectedItemsAction());
            },
          });
        }
      };
    },
    [selectedBoardItems, selectedMediaClipItems, selectedFileItems, showBulkDownloadAssetsModal, dispatch],
  );

  const createAssetDownloadHandler = useCallback(
    <TDownloadAsset extends CallableFunction>(
      downloadAsset: TDownloadAsset,
      additionalParams: Record<string, unknown> = {},
    ) => {
      return (id: string) => {
        downloadAsset({
          id,
          clipId: id,
          trackLocation: 'action-bar',
          ...additionalParams,
        });
      };
    },
    [],
  );

  const createBoardDownloadHandler = useCallback(
    <TDownloadBoard extends CallableFunction>(
      downloadBoard: TDownloadBoard,
      additionalParams: Record<string, unknown> = {},
    ) => {
      return (boardId: string) => {
        downloadBoard({
          boardId,
          trackLocation: 'action-bar',
          ...additionalParams,
        });
      };
    },
    [],
  );

  return {
    createBulkDownloadHandler,
    createAssetDownloadHandler,
    createBoardDownloadHandler,
  };
};
